///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type TileId } from "@/earthbrowser/activities/EarthActivity/tilecache/TileId"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type TileElevationMetadata } from "@/earthbrowser/utils/loaders/ElevationLoader"
import { cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { ElementGroup, IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Mesh vertices per tile edge by zoom level, used until the tile's elevation
// (and its min/max metadata) has loaded
const cMeshResolution = [128,64,64,64,64,64,64,64,64,64,64,64,64,64,32,32,32]

// Mountainous tiles are promoted to this resolution so ridgelines and
// silhouettes in tilted views follow the elevation data (which has ~4x the
// detail of the default mesh) instead of being cut by 64-vertex facets
const cFineMeshResolution = 128
// Promote when the tile's elevation range exceeds this fraction of its
// ground width: a 5% end-to-end relief ratio means real mountains, while
// rolling hills and plains keep the cheaper mesh
const cFineMeshReliefRatio = 0.05
// Below this level the camera is too high for silhouettes to matter
const cFineMeshMinLevel = 8

/**
 * Mesh vertices per edge for a tile: the per-level default, or the fine
 * resolution once mountainous relief is known from the elevation metadata.
 */
export function meshResolutionFor(tileId: TileId, metadata?: TileElevationMetadata): number {
	const base = cMeshResolution[tileId.z] ?? 32
	if(!metadata || tileId.z < cFineMeshMinLevel) {
		return base
	}
	const latMid = tileId.origin.y + tileId.delta.y / 2
	const widthMeters = Math.abs(tileId.delta.x) * Math.cos(latMid) * wgs.a * 1000
	const relief = metadata.max - metadata.min
	return relief / widthMeters > cFineMeshReliefRatio ? Math.max(base, cFineMeshResolution) : base
}

export class EarthTileGeometry implements Geometry {
	public tileId: TileId
	public sections: number
	public resolution: number

	public coordinate: EarthTileVertexBuffer
	public indexBuffer: EarthTileIndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube = new cube(-wgs.a/2, wgs.a/2)
	public boundingSphere: sphere = this.bounds.sphere
	public available: boolean = true

	constructor(context: EarthBrowserContext, tileId: TileId, sections: number, resolution: number = meshResolutionFor(tileId)) {
		this.tileId = tileId
		this.sections = sections
		this.resolution = resolution
		this.coordinate = new EarthTileVertexBuffer(context, tileId.origin, tileId.delta, resolution)
		this.indexBuffer = context.indexBuffer(sections, resolution, true)
		this.vertexBuffer = new VertexBuffer(context, [this.coordinate])
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}

// Vertex layout: [longitude, latitude, skirtFlag]
// The skirt flag is 0 for surface vertices and 1 for the duplicated boundary ring,
// which the vertex shader drops below the terrain to hide cracks between tiles.
class EarthTileVertexBuffer extends VertexAttribute {
	constructor(context: StovokorContext, origin: vec2, delta: vec2, resolution: number) {
		super(context, 3)
		const verts = this.values
		const wdelta = delta.x / resolution
		const hdelta = delta.y / resolution

		// Calculate a grid quantum that works across all zoom levels
		// We need to find a quantum that all zoom/resolution combinations divide evenly into
		// The finest grid comes from the highest zoom level with highest resolution per tile
		//
		// Key insight: For vertex alignment, we need the quantum to be a common divisor
		// of all (delta / resolution) values across zoom levels.
		//
		// At zoom z with resolution r:
		//   vertex_spacing = (π / 2^z) / r
		//
		// For alignment, we need: quantum = π / (2^z * r * k) where k is an integer
		// The finest practical quantum considering all zoom levels and resolutions:
		//   - Zoom 0: π / (1 * 128)
		//   - Zoom 13: π / (8192 * 64)
		//   - Zoom 16: π / (65536 * 32)
		//
		// The LCM approach: quantum = π / LCM(all denominators)
		// For practical purposes, using a power of 2: π / 2^20 works well
		const gridQuantum = Math.PI / (1 << 20) // ~3e-6 radians (~20cm at equator)

		const snapToGrid = (value: number) => {
			// Snap to the global grid to ensure vertex alignment across all zoom levels
			return Math.round(value / gridQuantum) * gridQuantum
		}

		for(let i = 0; i <= resolution; ++i) {
			const latitude = snapToGrid(origin.y + i*hdelta)
			for(let j = 0; j <= resolution; ++j) {
				const longitude = snapToGrid(origin.x + j*wdelta)
				verts.push(longitude, latitude, 0)
			}
		}

		// Skirt ring: duplicates of the boundary vertices flagged with skirtFlag=1.
		// Order must match EarthTileIndexBuffer: north (i=0), south (i=resolution),
		// west (j=0), east (j=resolution), each traversed by increasing index.
		const north = snapToGrid(origin.y)
		const south = snapToGrid(origin.y + resolution*hdelta)
		const west = snapToGrid(origin.x)
		const east = snapToGrid(origin.x + resolution*wdelta)
		for(let j = 0; j <= resolution; ++j) {
			verts.push(snapToGrid(origin.x + j*wdelta), north, 1)
		}
		for(let j = 0; j <= resolution; ++j) {
			verts.push(snapToGrid(origin.x + j*wdelta), south, 1)
		}
		for(let i = 0; i <= resolution; ++i) {
			verts.push(west, snapToGrid(origin.y + i*hdelta), 1)
		}
		for(let i = 0; i <= resolution; ++i) {
			verts.push(east, snapToGrid(origin.y + i*hdelta), 1)
		}
	}
}

export class EarthTileIndexBuffer extends IndexBuffer {
	constructor(context: StovokorContext, sections: number, resolution: number, skirt: boolean = false) {
		super(context)
		this.unpurgable = true

		const rows = Math.sqrt(sections)
		const cols = rows
		const rowWidth = resolution + 1
		const sectionWidth = resolution / rows
		const sectionHeight = resolution / cols

		// Skirt vertices appended after the surface grid by EarthTileVertexBuffer,
		// one run per boundary edge: north (i=0), south (i=resolution), west (j=0), east (j=resolution)
		const gridCount = rowWidth * rowWidth
		const northBase = gridCount
		const southBase = gridCount + rowWidth
		const westBase = gridCount + 2*rowWidth
		const eastBase = gridCount + 3*rowWidth

		let idx: number = 0

		// Wall quad between two surface edge vertices (g1, g2) and their skirt duplicates (s1, s2).
		// With g1->g2 traversed so the tile interior is on the right, this winding is CCW
		// from outside the globe, matching the surface front-face orientation.
		const wall = (g1: number, g2: number, s1: number, s2: number) => {
			this.triangle(s1, g1, g2)
			this.triangle(s1, g2, s2)
			idx += 6
		}

		const sectionIndices: number[] = []
		for(let row=0; row < rows; ++row) {
			for(let col=0; col < cols; ++col) {

				sectionIndices.push(idx)
				for(let srow=0; srow < sectionHeight; ++srow) {
					const row_idx = (row * sectionHeight + srow) * rowWidth
					const nrow_idx = row_idx + rowWidth
					for(let scol=0; scol < sectionWidth; ++scol) {
						const col_idx = col * sectionWidth + scol
						this.triangle(row_idx + col_idx, nrow_idx + col_idx, row_idx + col_idx+1)
						this.triangle(row_idx + col_idx+1, nrow_idx + col_idx, nrow_idx + col_idx+1)
					}
				}
				idx += sectionWidth * sectionHeight * 6

				// Each section owns the portions of the tile boundary it touches, so skirts
				// are hidden along with their section when a child tile covers it
				if(skirt) {
					if(row === 0) {
						for(let j = col*sectionWidth; j < (col+1)*sectionWidth; ++j) {
							wall(j, j+1, northBase + j, northBase + j+1)
						}
					}
					if(row === rows-1) {
						const base = resolution * rowWidth
						for(let j = (col+1)*sectionWidth; j > col*sectionWidth; --j) {
							wall(base + j, base + j-1, southBase + j, southBase + j-1)
						}
					}
					if(col === 0) {
						for(let i = (row+1)*sectionHeight; i > row*sectionHeight; --i) {
							wall(i*rowWidth, (i-1)*rowWidth, westBase + i, westBase + i-1)
						}
					}
					if(col === cols-1) {
						for(let i = row*sectionHeight; i < (row+1)*sectionHeight; ++i) {
							wall(i*rowWidth + resolution, (i+1)*rowWidth + resolution, eastBase + i, eastBase + i+1)
						}
					}
				}
			}
		}
		sectionIndices.push(idx)

		// set the section groups
		this.groups = [new ElementGroup(sectionIndices[1], 0)]
		for(let i=1; i < sections; ++i) {
			this.groups.push(new ElementGroup(sectionIndices[i+1] - sectionIndices[i], sectionIndices[i]))
		}
	}
}
